import React, { useState } from 'react';
import { UndoOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import {
	DESIGN_PRESET_BORDER_STYLES,
	parseDesignPresetBorderStyles,
	setDesignPresetBorderStyles,
	type BorderSideIndex,
} from 'common/design-tokens/borders';
import css from './DesignPresetsEditor.scss';

const sides = ['top', 'right', 'bottom', 'left'] as const;
const allSides: BorderSideIndex[] = [0, 1, 2, 3];

export default function DesignPresetBorderControl({
	value,
	assigned,
	disabled,
	onChange,
}: {
	value?: string;
	assigned: boolean;
	disabled?: boolean;
	onChange: (value: string | undefined) => void;
}) {
	const [selected, setSelected] = useState<BorderSideIndex[]>(allSides);
	const styles = parseDesignPresetBorderStyles(value ?? 'none');
	const current = selected.length ? styles[selected[0]] : undefined;
	const mixed = selected.some(side => styles[side] !== current);
	return (
		<div className={css.borderControl}>
			<div className={css.borderSides} role="group" aria-label="Border sides">
				{allSides.map(side => (
					<Tooltip key={side} title={sides[side]}>
						<button
							type="button"
							data-side={sides[side]}
							disabled={disabled}
							aria-label={`Select ${sides[side]} border`}
							aria-pressed={selected.includes(side)}
							onClick={() =>
								setSelected(selection =>
									selection.includes(side)
										? selection.filter(item => item !== side)
										: [...selection, side]
								)
							}
						>
							<span className={css.borderSideIcon} data-side={sides[side]} />
						</button>
					</Tooltip>
				))}
				<Tooltip title="All sides">
					<button
						type="button"
						data-side="all"
						disabled={disabled}
						aria-label="Select all border sides"
						aria-pressed={selected.length === 4}
						onClick={() => setSelected(allSides)}
					>
						<span className={css.borderSideIcon} data-side="all" />
					</button>
				</Tooltip>
			</div>
			<div className={css.borderStyle}>
				<span className={css.muted}>{mixed ? 'Mixed styles' : value ? 'Style' : 'Choose a style'}</span>
				<div className={css.borderStyleOptions} role="group" aria-label="Border line style">
					{DESIGN_PRESET_BORDER_STYLES.map(style => (
						<Tooltip key={style} title={style}>
							<button
								type="button"
								disabled={disabled || !selected.length}
								aria-label={`Border style: ${style}`}
								aria-pressed={value !== undefined && !mixed && current === style}
								onClick={() => onChange(setDesignPresetBorderStyles(value, selected, style))}
							>
								<span className={css.borderLine} data-style={style}>
									{style === 'none' ? '×' : null}
								</span>
							</button>
						</Tooltip>
					))}
					{assigned && (
						<Tooltip title="Clear style to inherit">
							<button
								type="button"
								disabled={disabled}
								aria-label="Clear border style"
								onClick={() => onChange(undefined)}
							>
								<UndoOutlined />
							</button>
						</Tooltip>
					)}
				</div>
			</div>
		</div>
	);
}
